import React, { useState, useEffect, useCallback } from 'react';
import {
  FiCalendar, FiAlertCircle, FiRefreshCw, FiClock, FiAward,
  FiEdit, FiCheckSquare, FiFileText, FiChevronLeft, FiChevronRight,
} from 'react-icons/fi';
import { getAuthHeaders } from '../utils/auth';
import { getApiEndpoint } from '../utils/api';
import './Calendar.css';

const TYPE_CONFIG = {
  grant_deadline:  { icon: FiAward,       label: 'Grant Deadline',   color: '#6366f1' },
  grant_submitted: { icon: FiAward,       label: 'Grant Submitted',  color: '#2563eb' },
  paper_deadline:  { icon: FiEdit,        label: 'Paper Deadline',   color: '#10b981' },
  paper_submitted: { icon: FiEdit,        label: 'Paper Submitted',  color: '#059669' },
  action_item:     { icon: FiCheckSquare, label: 'Task Due',         color: '#f59e0b' },
  nofo_deadline:   { icon: FiFileText,    label: 'NOFO Closes',      color: '#ef4444' },
};

const MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

function getDaysInMonth(year, month) {
  return new Date(year, month + 1, 0).getDate();
}

function getFirstDayOfWeek(year, month) {
  return new Date(year, month, 1).getDay(); // 0=Sun
}

const Calendar = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [viewMonth, setViewMonth] = useState(() => {
    const now = new Date();
    return { year: now.getFullYear(), month: now.getMonth() };
  });

  const fetchCalendar = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await fetch(getApiEndpoint('/api/dashboard/calendar?months=6'), {
        headers: getAuthHeaders(),
      });
      if (!res.ok) throw new Error('Failed to load calendar');
      setData(await res.json());
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchCalendar(); }, [fetchCalendar]);

  const prevMonth = () => {
    setViewMonth(prev => {
      const m = prev.month - 1;
      return m < 0 ? { year: prev.year - 1, month: 11 } : { year: prev.year, month: m };
    });
  };

  const nextMonth = () => {
    setViewMonth(prev => {
      const m = prev.month + 1;
      return m > 11 ? { year: prev.year + 1, month: 0 } : { year: prev.year, month: m };
    });
  };

  const goToToday = () => {
    const now = new Date();
    setViewMonth({ year: now.getFullYear(), month: now.getMonth() });
  };

  if (loading) {
    return <div className="cal-loading"><div className="spinner" /><p>Loading calendar...</p></div>;
  }

  if (error) {
    return (
      <div className="cal-error">
        <FiAlertCircle /> {error}
        <button onClick={fetchCalendar}><FiRefreshCw /> Retry</button>
      </div>
    );
  }

  if (!data) return null;

  const { events, upcoming, overdue } = data;
  const today = new Date();
  const todayStr = today.toISOString().split('T')[0];

  // Build day → events map for current view month
  const monthKey = `${viewMonth.year}-${String(viewMonth.month + 1).padStart(2, '0')}`;
  const monthEvents = data.months[monthKey] || [];
  const dayEventsMap = {};
  for (const ev of monthEvents) {
    const day = parseInt(ev.date.split('-')[2], 10);
    if (!dayEventsMap[day]) dayEventsMap[day] = [];
    dayEventsMap[day].push(ev);
  }

  const daysInMonth = getDaysInMonth(viewMonth.year, viewMonth.month);
  const firstDay = getFirstDayOfWeek(viewMonth.year, viewMonth.month);

  return (
    <div className="cal-page">

      {/* Alerts bar */}
      {(overdue.length > 0 || upcoming.length > 0) && (
        <div className="cal-alerts">
          {overdue.length > 0 && (
            <div className="cal-alert cal-alert--overdue">
              <FiAlertCircle />
              <strong>{overdue.length} overdue</strong>
              <span>— {overdue.map(e => e.title.replace(/^(Decision deadline|Submitted): /, '')).slice(0, 2).join(', ')}{overdue.length > 2 ? ` + ${overdue.length - 2} more` : ''}</span>
            </div>
          )}
          {upcoming.length > 0 && (
            <div className="cal-alert cal-alert--upcoming">
              <FiClock />
              <strong>{upcoming.length} upcoming</strong>
              <span>in the next 30 days</span>
            </div>
          )}
        </div>
      )}

      <div className="cal-layout">

        {/* Calendar grid */}
        <div className="cal-main">
          <div className="cal-nav">
            <button className="cal-nav-btn" onClick={prevMonth}><FiChevronLeft /></button>
            <h2>{MONTH_NAMES[viewMonth.month]} {viewMonth.year}</h2>
            <button className="cal-nav-btn" onClick={nextMonth}><FiChevronRight /></button>
            <button className="cal-today-btn" onClick={goToToday}>Today</button>
          </div>

          <div className="cal-grid">
            {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(d => (
              <div key={d} className="cal-day-header">{d}</div>
            ))}
            {Array.from({ length: firstDay }, (_, i) => (
              <div key={`empty-${i}`} className="cal-day cal-day--empty" />
            ))}
            {Array.from({ length: daysInMonth }, (_, i) => {
              const day = i + 1;
              const dateStr = `${viewMonth.year}-${String(viewMonth.month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
              const isToday = dateStr === todayStr;
              const dayEvents = dayEventsMap[day] || [];

              return (
                <div key={day} className={`cal-day ${isToday ? 'cal-day--today' : ''} ${dayEvents.length > 0 ? 'cal-day--has-events' : ''}`}>
                  <span className="cal-day-num">{day}</span>
                  {dayEvents.slice(0, 3).map((ev, j) => {
                    const cfg = TYPE_CONFIG[ev.type] || TYPE_CONFIG.action_item;
                    return (
                      <div key={j} className="cal-event" style={{ borderLeftColor: cfg.color }} title={ev.title}>
                        {ev.title.length > 25 ? ev.title.slice(0, 25) + '...' : ev.title}
                      </div>
                    );
                  })}
                  {dayEvents.length > 3 && (
                    <span className="cal-more">+{dayEvents.length - 3} more</span>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* Sidebar: upcoming + overdue */}
        <div className="cal-sidebar">
          <div className="cal-sidebar-section">
            <h3><FiClock /> Upcoming Deadlines</h3>
            {upcoming.length === 0 ? (
              <p className="cal-sidebar-empty">No upcoming deadlines</p>
            ) : (
              <ul className="cal-event-list">
                {upcoming.map((ev, i) => {
                  const cfg = TYPE_CONFIG[ev.type] || TYPE_CONFIG.action_item;
                  const Icon = cfg.icon;
                  return (
                    <li key={i} className="cal-event-item">
                      <Icon className="cal-event-icon" style={{ color: cfg.color }} />
                      <div className="cal-event-body">
                        <span className="cal-event-title">{ev.title.replace(/^(Decision deadline|Submitted): /, '')}</span>
                        {ev.subtitle && <span className="cal-event-sub">{ev.subtitle}</span>}
                      </div>
                      <span className={`cal-event-days ${ev.urgency <= 7 ? 'cal-event-days--urgent' : ''}`}>
                        {ev.urgency === 0 ? 'Today' : `${ev.urgency}d`}
                      </span>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>

          {overdue.length > 0 && (
            <div className="cal-sidebar-section">
              <h3 className="cal-overdue-title"><FiAlertCircle /> Overdue</h3>
              <ul className="cal-event-list">
                {overdue.map((ev, i) => {
                  const cfg = TYPE_CONFIG[ev.type] || TYPE_CONFIG.action_item;
                  const Icon = cfg.icon;
                  return (
                    <li key={i} className="cal-event-item cal-event-item--overdue">
                      <Icon className="cal-event-icon" style={{ color: '#ef4444' }} />
                      <div className="cal-event-body">
                        <span className="cal-event-title">{ev.title.replace(/^(Decision deadline|Submitted): /, '')}</span>
                        {ev.subtitle && <span className="cal-event-sub">{ev.subtitle}</span>}
                      </div>
                      <span className="cal-event-days cal-event-days--overdue">
                        {Math.abs(ev.urgency)}d ago
                      </span>
                    </li>
                  );
                })}
              </ul>
            </div>
          )}

          {/* Legend */}
          <div className="cal-sidebar-section">
            <h3>Legend</h3>
            <div className="cal-legend">
              {Object.entries(TYPE_CONFIG).map(([key, cfg]) => (
                <div key={key} className="cal-legend-item">
                  <span className="cal-legend-dot" style={{ background: cfg.color }} />
                  {cfg.label}
                </div>
              ))}
            </div>
          </div>
        </div>

      </div>
    </div>
  );
};

export default Calendar;
